import { ChatMode } from '@/components/chat/types/types';
import { Button } from '@heidi/design-system/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@heidi/design-system/drawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { cn } from '@/lib/cn';
import { useTranslations } from '@heidi/runtime/i18n';
import { Evidence } from '@heidi/design-system/icons';
import { Check, Wand, X, Zap } from 'lucide-react';
import { useCallback, useState } from 'react';

interface ModeToggleButtonProps {
  mode: ChatMode;
  onModeChange: (mode: ChatMode) => void;
  disabled?: boolean;
  isMobile?: boolean;
  hideText?: boolean;
}

const ModeToggleButton = ({
  mode,
  onModeChange,
  disabled,
  isMobile = false,
  hideText = false,
}: ModeToggleButtonProps) => {
  const { t } = useTranslations();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const getModeIcon = useCallback(() => {
    if (mode === ChatMode.EVIDENCE) return <Evidence size={14} />;
    if (mode === ChatMode.AUTO) return <Zap size={14} />;
    return <Wand size={14} />;
  }, [mode]);

  const getModeLabel = useCallback(() => {
    if (mode === ChatMode.EVIDENCE) return t('askAI.modeToggle.evidence');
    if (mode === ChatMode.AUTO) return t('askAI.modeToggle.auto');
    return t('askAI.modeToggle.askHeidi');
  }, [mode, t]);

  const button = (
    <Button
      type="button"
      variant="outline"
      size={isMobile ? 'lg' : 'sm'}
      disabled={disabled}
      className={cn(
        'border border-border-pressed bg-background-tertiary-pressed font-normal text-text-primary hover:bg-background-tertiary-pressed',
        disabled && 'disabled:text-text-primary disabled:opacity-100'
      )}
      iconOnly={hideText}
    >
      {getModeIcon()}
      {!hideText && getModeLabel()}
    </Button>
  );

  const content = (
    <div className="w-full px-4 pb-6 pt-2">
      <div className="mb-4 flex items-center justify-between px-1 pt-2">
        <h3 className="text-lg font-medium">{t('askAI.modeToggle.chooseMode')}</h3>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          disableAutomaticSvgSizing
          onClick={() => setDrawerOpen(false)}
          className="size-8 p-0"
        >
          <X className="!size-[18px]" />
        </Button>
      </div>
      <div className="space-y-2">
        <div
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-md p-3 transition-colors hover:bg-background-tertiary-hover',
            mode === ChatMode.LEGACY
              ? 'border-2 border-border-pressed'
              : 'border border-border'
          )}
          onClick={() => {
            onModeChange(ChatMode.LEGACY);
            if (isMobile) setDrawerOpen(false);
          }}
        >
          <Wand className="size-4 shrink-0" />
          <div className="flex flex-1 flex-col gap-1">
            <span className="text-sm font-medium text-text-primary">
              {t('askAI.modeToggle.legacyMode.title')}
            </span>
            <span className="text-xs text-text-secondary">
              {t('askAI.modeToggle.legacyMode.descriptionMobile')}
            </span>
          </div>
          {mode === ChatMode.LEGACY && <Check className="size-4 shrink-0" />}
        </div>
        <div
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-md p-3 transition-colors hover:bg-background-tertiary-hover',
            mode === ChatMode.EVIDENCE
              ? 'border-2 border-border-pressed'
              : 'border border-border'
          )}
          onClick={() => {
            onModeChange(ChatMode.EVIDENCE);
            if (isMobile) setDrawerOpen(false);
          }}
        >
          <Evidence className="size-4 shrink-0" />
          <div className="flex flex-1 flex-col gap-1">
            <span className="text-sm font-medium">
              {t('askAI.modeToggle.evidenceMode.title')}
            </span>
            <span className="text-xs text-text-secondary">
              {t('askAI.modeToggle.evidenceMode.descriptionMobile')}
            </span>
          </div>
          {mode === ChatMode.EVIDENCE && <Check className="size-4 shrink-0" />}
        </div>
        <div
          className={cn(
            'flex cursor-pointer items-center gap-2 rounded-md p-3 transition-colors hover:bg-background-tertiary-hover',
            mode === ChatMode.AUTO
              ? 'border-2 border-border-pressed'
              : 'border border-border'
          )}
          onClick={() => {
            onModeChange(ChatMode.AUTO);
            if (isMobile) setDrawerOpen(false);
          }}
        >
          <Zap className="size-4 shrink-0" />
          <div className="flex flex-1 flex-col gap-1">
            <span className="text-sm font-medium text-text-primary">
              {t('askAI.modeToggle.autoMode.title')}
            </span>
            <span className="text-xs text-text-secondary">
              {t('askAI.modeToggle.autoMode.descriptionMobile')}
            </span>
          </div>
          {mode === ChatMode.AUTO && <Check className="size-4 shrink-0" />}
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
        <DrawerTrigger asChild>{button}</DrawerTrigger>
        <DrawerContent className="max-h-[80vh]" hideCloseIcon>
          <DrawerHeader className="sr-only">
            <DrawerTitle>{t('askAI.modeToggle.chooseMode')}</DrawerTitle>
          </DrawerHeader>
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{button}</DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={8} className="w-80">
        <DropdownMenuRadioGroup
          value={mode}
          onValueChange={(value) => onModeChange(value as ChatMode)}
        >
          <DropdownMenuRadioItem
            value={ChatMode.LEGACY}
            className="flex items-center gap-2 p-2 [&>span:first-child]:hidden"
          >
            <Wand className="size-4 shrink-0" />
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-sm font-medium text-text-primary">
                {t('askAI.modeToggle.legacyMode.title')}
              </span>
              <span className="text-xs text-text-secondary">
                {t('askAI.modeToggle.legacyMode.descriptionDesktop')}
              </span>
            </div>
            {mode === ChatMode.LEGACY && <Check className="size-4 shrink-0" />}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem
            value={ChatMode.EVIDENCE}
            className="flex items-center gap-2 p-2 [&>span:first-child]:hidden"
          >
            <Evidence className="size-4 shrink-0" />
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-sm font-medium">
                {t('askAI.modeToggle.evidenceMode.title')}
              </span>
              <span className="text-xs text-text-secondary">
                {t('askAI.modeToggle.evidenceMode.descriptionDesktop')}
              </span>
            </div>
            {mode === ChatMode.EVIDENCE && <Check className="size-4 shrink-0" />}
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem
            value={ChatMode.AUTO}
            className="flex items-center gap-2 p-2 [&>span:first-child]:hidden"
          >
            <Zap className="size-4 shrink-0" />
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-sm font-medium text-text-primary">
                {t('askAI.modeToggle.autoMode.title')}
              </span>
              <span className="text-xs text-text-secondary">
                {t('askAI.modeToggle.autoMode.descriptionDesktop')}
              </span>
            </div>
            {mode === ChatMode.AUTO && <Check className="size-4 shrink-0" />}
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ModeToggleButton;
